import React from 'react';
import {
  AccordionShadow,
  Accordions,
  AccordionsItem,
  AccordionsItemHeader,
  AccordionsItemSection,
  CondensedMedium,
  Dropdown,
  Flashy,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Icon,
  Layout,
  SmallHeader,
  TaxonomyUtil,
  Textarea,
  useBreakpoint,
} from '@axiom/ui';
import {
  levelOfExperienceValues,
  selfServiceAdditionalDetailsForTalentMaxLength,
} from '@axiom/const';
import type {
  Language,
  ProjectNeedsWithLegalSkills,
  Taxonomy,
} from '@axiom/validation';

const sortDropDownOptions = (
  a: { label: string; value: string },
  b: { label: string; value: string }
) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1);

export type DirectEngagementsFormAdditionalInfoProps = {
  expanded: boolean;
  isValid: boolean;
  languages: Language[];
  submitCount: number;
  taxonomy: Taxonomy;
  values: Partial<ProjectNeedsWithLegalSkills>;
};

export const DirectEngagementsFormAdditionalInfo = ({
  expanded,
  isValid,
  languages,
  submitCount,
  taxonomy,
  values,
}: DirectEngagementsFormAdditionalInfoProps) => {
  const { isMobile } = useBreakpoint();
  const { generalSkills, legalTechSkills } = taxonomy;
  const industryOptions = TaxonomyUtil.getFilteredIntakeFormIndustryOptions(
    taxonomy.industries
  );

  return (
    <Accordions
      shadow={AccordionShadow.DEFAULT}
      name="Additional_info_accordion"
    >
      <AccordionsItem name="Additional_info" expanded={expanded}>
        <AccordionsItemHeader>
          <Grid verticalAlign="middle" stackable>
            <GridColumn largeScreenWidth={9}>
              <SmallHeader>
                Any additional skills or requirements? (optional)
              </SmallHeader>
            </GridColumn>
            {!isValid &&
              submitCount > 0 &&
              (values.additionalDetailsForTalent ?? []).length >
                selfServiceAdditionalDetailsForTalentMaxLength && (
                <GridColumn largeScreenWidth={3}>
                  <Gutter top="8px" only="mobile" />
                  <Layout
                    direction="horizontal"
                    position={isMobile ? 'middle' : 'right'}
                    name="other_talent_warning"
                  >
                    <Flashy color="controlAlert">
                      <Icon name="alert-filled" />
                    </Flashy>
                    <Gutter right="8px" />
                    <CondensedMedium>
                      <Flashy color="controlAlert">Resolve errors</Flashy>
                    </CondensedMedium>

                    <Gutter right="8px" />
                  </Layout>
                </GridColumn>
              )}
          </Grid>
        </AccordionsItemHeader>
        <AccordionsItemSection>
          <Grid>
            <GridRow gutterVertical="24px">
              <GridColumn
                largeScreenWidth={4}
                smallScreenWidth={12}
                tabletWidth={4}
              >
                <Dropdown
                  name="levelOfExperience"
                  label="Minimum years of experience"
                  options={Object.keys(levelOfExperienceValues).map(
                    (key, index) => ({
                      label: Object.values(levelOfExperienceValues)[index],
                      value: key,
                    })
                  )}
                  displayKey="label"
                  valueKey="value"
                  disableArrowKeyChange
                />
              </GridColumn>
              <GridColumn
                largeScreenWidth={5}
                smallScreenWidth={12}
                tabletWidth={5}
              >
                <Gutter top="24px" only="mobile" />
                <Dropdown
                  name="industry"
                  label="Industry experience (if required for this work)"
                  options={industryOptions}
                  displayKey="label"
                  valueKey="value"
                  disableArrowKeyChange
                />
              </GridColumn>
              <GridColumn
                largeScreenWidth={3}
                smallScreenWidth={12}
                tabletWidth={3}
              >
                <Gutter top="24px" only="mobile" />
                <Dropdown
                  name="languageId"
                  label="Language"
                  options={languages.map(language => ({
                    label: language.name,
                    value: language.id,
                  }))}
                  displayKey="label"
                  valueKey="value"
                  disableArrowKeyChange
                />
              </GridColumn>
            </GridRow>
            <GridRow gutterBottom="24px">
              <GridColumn
                largeScreenWidth={6}
                smallScreenWidth={12}
                tabletWidth={6}
              >
                <Dropdown
                  name="legalTechSkills"
                  label="Legal tech skills"
                  options={TaxonomyUtil.getLegalTechSkillOptions(
                    legalTechSkills
                  ).sort((a, b) => sortDropDownOptions(a, b))}
                  displayKey="label"
                  valueKey="value"
                  disableArrowKeyChange
                />
              </GridColumn>
              <GridColumn
                largeScreenWidth={6}
                smallScreenWidth={12}
                tabletWidth={6}
              >
                <Gutter top="24px" only="mobile" />
                <Dropdown
                  name="generalSkills"
                  label="Other relevant skills"
                  options={TaxonomyUtil.getGeneralSkillOptions(
                    generalSkills
                  ).sort((a, b) => sortDropDownOptions(a, b))}
                  displayKey="label"
                  valueKey="value"
                  disableArrowKeyChange
                />
              </GridColumn>
            </GridRow>
            <GridRow>
              <GridColumn>
                <Textarea
                  name="additionalDetailsForTalent"
                  label="More details about what this work involves or why it's needed"
                />
              </GridColumn>
            </GridRow>
          </Grid>
        </AccordionsItemSection>
      </AccordionsItem>
    </Accordions>
  );
};
